import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check as CheckIcon, Clock, ExternalLink, ImageUp, Maximize2, ScanText, ShieldAlert, X } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthedImage } from '@/components/applicant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { checkFile, FileDropzone, type FileRules } from '@/components/ui/dropzone';
import { Field, Input, Textarea } from '@/components/ui/input';
import { adminApi, checksApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { Check, IcpResult } from '@/lib/api/types';
import { ME_KEY } from '@/lib/auth';
import { cn, formatDateTime } from '@/lib/utils';
import { canSendScreenshot } from '@/lib/visa';

/** The portal page the automation uses (the customer opens it themselves). */
export const ICP_PORTAL_URL = 'https://beta.smartservices.icp.gov.ae/echannels/web/client/default.html#/fileValidity';

export const SCREENSHOT_RULES: FileRules = {
  accept: ['image/jpeg', 'image/png', 'image/webp'],
  maxBytes: 10 * 1024 * 1024,
  typeMessage: 'Use a JPEG, PNG or WEBP screenshot.',
};

/** The uploaded screenshot: a thumbnail that opens full size. */
function ScreenshotImage({ check, className }: { check: Check; className?: string }) {
  const img = useAuthedImage(check.screenshot_url, check.screenshot_uploaded_at ?? 0);
  const [open, setOpen] = React.useState(false);
  if (!check.screenshot_url) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn('group relative block overflow-hidden rounded-xl border bg-secondary/40', className)}
        title="Open full size"
      >
        {img.url ? (
          <img src={img.url} alt="Portal screenshot" className="max-h-80 w-full object-cover object-top" />
        ) : (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">{img.loading ? 'Loading…' : 'No image'}</div>
        )}
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/85 px-2 py-1 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100">
          <Maximize2 className="size-3" /> Full size
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Portal screenshot</DialogTitle>
            <DialogDescription>Uploaded {formatDateTime(check.screenshot_uploaded_at)}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-auto rounded-lg border">{img.url && <img src={img.url} alt="Portal screenshot, full size" className="w-full" />}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Customer: send a screenshot when the automation gave up
// ---------------------------------------------------------------------------

export function ScreenshotPanel({ check, isOwn, cost }: { check: Check; isOwn: boolean; cost: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [file, setFile] = React.useState<File | null>(null);
  const fileError = file ? checkFile(file, SCREENSHOT_RULES) : null;

  const send = useMutation({
    mutationFn: () => checksApi.uploadScreenshot(check.id, file!),
    onSuccess: (c) => {
      qc.setQueryData(['check', check.id], c);
      qc.invalidateQueries({ queryKey: ME_KEY });
      qc.invalidateQueries({ queryKey: ['checks'] });
      setFile(null);
      toast.success('Screenshot sent', { description: 'An admin will read the result from it.' });
    },
    onError: (err) => {
      if (isApiError(err, 402)) {
        toast.error('Not enough coins', { action: { label: 'Buy coins', onClick: () => navigate('/coins/buy') } });
      } else {
        toast.error(userMessage(err, 'Upload failed'));
      }
    },
  });

  if (check.screenshot_status === 'pending') {
    return (
      <div className="space-y-3 rounded-xl border border-sky-500/30 bg-sky-500/[0.05] p-4">
        <p className="flex items-center gap-2 font-semibold">
          <Clock className="size-4 text-sky-600" /> Your screenshot is waiting for review
        </p>
        <p className="text-sm text-muted-foreground">
          Sent {formatDateTime(check.screenshot_uploaded_at)}. An admin compares it with your passport details and enters the result. If they can't use it, your coins come back.
        </p>
        <ScreenshotImage check={check} />
      </div>
    );
  }
  if (!canSendScreenshot(check) || !isOwn) return null;

  const declined = check.screenshot_status === 'declined';
  return (
    <div className="space-y-4 rounded-xl border p-4">
      <div>
        <p className="flex items-center gap-2 font-semibold">
          <ImageUp className="size-4 text-primary-ink" /> {declined ? 'Send a new screenshot' : 'Still no answer? Send us a screenshot'}
        </p>
        {declined && check.screenshot_note && (
          <p className="mt-2 rounded-lg bg-rose-500/[0.07] p-3 text-sm text-rose-700 dark:text-rose-300">
            <span className="font-semibold">Your last screenshot was declined:</span> {check.screenshot_note}
          </p>
        )}
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            Open the{' '}
            <a href={ICP_PORTAL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary-ink hover:underline">
              ICP File Validity page <ExternalLink className="size-3" />
            </a>{' '}
            and search with passport <span className="font-mono font-semibold text-foreground">{check.passport_no}</span>.
          </li>
          <li>When your file appears, take a <strong>full-page</strong> screenshot: the passport number, File No., File Status and the dates must all be visible.</li>
          <li>Upload it here. An admin reads the result from it, and an ACTIVE file unlocks your status report.</li>
        </ol>
      </div>
      <FileDropzone
        file={file}
        onFile={setFile}
        accept={SCREENSHOT_RULES.accept}
        title="Drop the screenshot here, or click to choose"
        hint="JPEG, PNG or WEBP, up to 10 MB"
        error={fileError ?? undefined}
        icon={<ImageUp />}
        disabled={send.isPending}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Costs {cost} coin{cost === 1 ? '' : 's'}, refunded if the screenshot can't be used.
        </p>
        <Button onClick={() => send.mutate()} disabled={!file || !!fileError} loading={send.isPending}>
          {!send.isPending && <ImageUp />} Send screenshot
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin: read the screenshot, enter the result, approve or decline
// ---------------------------------------------------------------------------

const RESULT_FIELDS: { key: keyof IcpResult; label: string; hint?: string; mono?: boolean }[] = [
  { key: 'file_status', label: 'File status', hint: 'Exactly as shown, e.g. ACTIVE' },
  { key: 'file_no', label: 'File No.', mono: true },
  { key: 'unified_number', label: 'Unified No.', mono: true },
  { key: 'issuance_date', label: 'File issuance date', hint: 'dd/mm/yyyy', mono: true },
  { key: 'last_entry_date', label: 'Last date allowed to enter', hint: 'dd/mm/yyyy', mono: true },
];

const DATE_RE = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

export function ScreenshotReview({ check }: { check: Check }) {
  const qc = useQueryClient();
  const ocr = check.screenshot_ocr;
  const [values, setValues] = React.useState<IcpResult>({
    file_status: ocr?.file_status ?? '',
    file_no: ocr?.file_no ?? '',
    unified_number: ocr?.unified_number ?? '',
    issuance_date: ocr?.issuance_date ?? '',
    last_entry_date: ocr?.last_entry_date ?? '',
  });
  const [note, setNote] = React.useState('');
  const [declining, setDeclining] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const done = (c: Check, message: string) => {
    qc.setQueryData(['check', check.id], c);
    qc.invalidateQueries({ queryKey: ['admin'] });
    toast.success(message);
  };
  const onError = (err: unknown) => {
    toast.error(userMessage(err, 'Failed'));
    if (isApiError(err, 409)) qc.invalidateQueries({ queryKey: ['check', check.id] });
  };
  const approve = useMutation({
    mutationFn: () => adminApi.approveScreenshot(check.id, { ...values, note: note.trim() || undefined }),
    onSuccess: (c) => done(c, `Approved: file status ${c.result?.file_status}`),
    onError,
  });
  const decline = useMutation({
    mutationFn: () => adminApi.declineScreenshot(check.id, reason.trim()),
    onSuccess: (c) => {
      setDeclining(false);
      done(c, 'Screenshot declined, coins refunded');
    },
    onError,
  });

  if (check.screenshot_status !== 'pending') return null;

  const errors: Partial<Record<keyof IcpResult, string>> = {};
  if (values.file_status.trim().length < 2) errors.file_status = 'Enter the file status shown on the screenshot.';
  for (const k of ['issuance_date', 'last_entry_date'] as const) {
    if (values[k].trim() && !DATE_RE.test(values[k].trim())) errors[k] = 'Use dd/mm/yyyy.';
  }
  const valid = Object.keys(errors).length === 0;
  const willBeActive = values.file_status.trim().toUpperCase() === 'ACTIVE';

  return (
    <div className="space-y-4 rounded-xl border-2 border-amber-500/40 bg-amber-500/[0.04] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-semibold">
          <ScanText className="size-4 text-amber-600" /> Review the customer's screenshot
        </p>
        {ocr &&
          (ocr.passport_no_seen ? (
            <Badge tone="success">Passport {check.passport_no} found on it</Badge>
          ) : (
            <Badge tone="danger">Passport {check.passport_no} not found on it</Badge>
          ))}
      </div>
      <p className="text-sm text-muted-foreground">
        {ocr
          ? 'The values below were read from the image automatically. Compare every one with the screenshot and correct it before approving.'
          : "The image couldn't be read automatically. Type the values from the screenshot."}
      </p>
      <ScreenshotImage check={check} />

      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) approve.mutate();
        }}
      >
        {RESULT_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} htmlFor={`ss-${f.key}`} hint={f.hint} error={errors[f.key]} className={f.key === 'file_status' ? 'sm:col-span-2' : undefined}>
            <Input
              id={`ss-${f.key}`}
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              className={cn(f.mono && 'font-mono', f.key === 'file_status' && 'uppercase')}
              aria-invalid={!!errors[f.key]}
            />
          </Field>
        ))}
        <Field label="Note (optional, the customer sees it)" htmlFor="ss-note" className="sm:col-span-2">
          <Input id="ss-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {willBeActive ? 'ACTIVE: the customer can download the status report.' : 'Not ACTIVE: the check completes without a report.'}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="text-destructive hover:text-destructive" onClick={() => { setReason(''); setDeclining(true); }}>
              <X /> Decline
            </Button>
            <Button type="submit" variant="success" disabled={!valid} loading={approve.isPending}>
              {!approve.isPending && <CheckIcon />} Approve result
            </Button>
          </div>
        </div>
      </form>

      <Dialog open={declining} onOpenChange={setDeclining}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="size-5 text-destructive" /> Decline screenshot
            </DialogTitle>
            <DialogDescription>
              @{check.username ?? 'the customer'} gets {check.coins_charged} coin{check.coins_charged === 1 ? '' : 's'} back and can send a new one.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (reason.trim().length >= 2) decline.mutate();
            }}
          >
            <Field label="Reason (the customer sees this)" htmlFor="ss-reason">
              <Textarea id="ss-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The passport number isn't visible" autoFocus />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeclining(false)}>Cancel</Button>
              <Button type="submit" variant="destructive" loading={decline.isPending} disabled={reason.trim().length < 2}>
                Decline and refund
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
